import cn from 'classnames';
import { HTMLAttributes, memo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './FieldsBlock.module.css';

type Props = HTMLAttributes<HTMLDivElement> & {
  headline: string;
  required?: boolean;
};

export const FieldsBlock =
  /**
   * Renders a block container with a headline and optional children.
   * Accepts standard HTML `div` attributes via `htmlAttributes`.
   * @param props - Component props.
   * @param props.headline - The headline text displayed at the top of the block.
   * @param props.required - Optional boolean that displays a red asterisk next to the headline.
   * @param props.children - Optional React nodes to be rendered inside the block.
   * @param props.htmlAttributes - Additional HTML attributes for the container `div`.
   * @returns FieldsBlock component.
   */
  memo(({ children, headline, required, ...htmlAttributes }: Props) => {
    const { t } = useTranslation();

    return (
      <div {...htmlAttributes} className={styles.fieldsBlock}>
        <legend
          className={cn(styles.fieldsTitle, { [styles.required]: required })}
        >
          {headline}

          {required && (
            <span className={styles.srOnly}>{t('common.required')}</span>
          )}
        </legend>
        {children}
      </div>
    );
  });

FieldsBlock.displayName = 'FieldsBlock';
